import { Briefcase, Users } from "lucide-react";
import {
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type ReactElement
} from "react";
import { useNavigate } from "react-router-dom";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from "@/components/ui/hover-card";
import { SignalBadge } from "@/features/agentAttention/components/SignalBadge";
import { itemRoute } from "@/features/agentAttention/items";
import {
  byUrgency,
  SIGNAL_META,
  signalAsk,
  signalFace,
  signalTone,
  TONE_CLASSES
} from "@/features/agentAttention/signalTone";
import type { AttentionItem } from "@/features/agentAttention/types";
import { OPEN_THREAD_STATE } from "@/features/agents/routes";
import { cn } from "@/lib/utils";

import {
  groupLabel,
  groupMark,
  groupSubtitle,
  isPageGroup,
  rowLabel,
  viewedAgo,
  type TabGroup
} from "../groups";
import type { AppTab } from "../types";
import { TabMark } from "./TabMark";

type Props = {
  group: TabGroup;
  signalsFor: (tabId: string) => AttentionItem[];
  isRunning: (tabId: string) => boolean;
  onSelect: (tab: AppTab) => void;
  onCloseGroup: () => void;
  /** Keeps the card shut, e.g. while a tab is being dragged. */
  disabled?: boolean;
  /** The tile; it opens the card on hover or focus, and ArrowDown moves into the rows. */
  children: ReactElement;
};

const ROW = "[data-hover-row]";

const Row: FC<{
  tab: AppTab;
  signals: AttentionItem[];
  running: boolean;
  onOpen: () => void;
  onReview: (item: AttentionItem) => void;
}> = ({ tab, signals, running, onOpen, onReview }) => {
  const top = signals.length ? byUrgency(signals)[0] : null;
  const tone = top ? signalTone(top) : null;
  const status = top
    ? `${SIGNAL_META[signalFace(top)].line}: ${signalAsk(top)}`
    : running
    ? "Working…"
    : viewedAgo(tab.visitedAt, Date.now()) || "Open";
  const Icon = tab.kind === "client" ? Users : Briefcase;
  return (
    <div
      role="button"
      tabIndex={-1}
      data-hover-row=""
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group/row flex h-11 w-full cursor-pointer items-center gap-2.5 rounded-md px-2 text-left outline-none hover:bg-muted focus-visible:bg-muted"
    >
      <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground group-hover/row:bg-background group-focus-visible/row:bg-background">
        <Icon className="size-3.5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-xs font-medium text-foreground">
          {rowLabel(tab)}
        </span>
        <span
          className={cn(
            "truncate text-xs",
            tone ? TONE_CLASSES[tone].text : "text-muted-foreground"
          )}
        >
          {status}
        </span>
      </span>
      {top && tone === "red" ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReview(top);
          }}
          className="hidden h-6 shrink-0 items-center rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground group-focus-within/row:flex group-hover/row:flex group-focus-visible/row:flex hover:bg-primary/90"
        >
          {SIGNAL_META[signalFace(top)].action}
        </button>
      ) : null}
      <SignalBadge items={signals} running={running} />
    </div>
  );
};

/**
 * A tile's hover card. A tile carries no name, so the card says who the
 * client is (photo or initials, name, citizenship and birth year), lists the
 * open tabs with what each agent is doing, and offers Review on a tab that
 * needs you. A page tile's card is its name alone.
 */
export const GroupHoverCard: FC<Props> = ({
  group,
  signalsFor,
  isRunning,
  onSelect,
  onCloseGroup,
  disabled = false,
  children
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const label = groupLabel(group);
  const page = isPageGroup(group);
  const subtitle = groupSubtitle(group);
  const n = group.tabs.length;

  const rows = () =>
    Array.from(contentRef.current?.querySelectorAll<HTMLElement>(ROW) ?? []);
  const focusRow = (delta: number) => {
    const list = rows();
    if (!list.length) return;
    const at = list.indexOf(document.activeElement as HTMLElement);
    const next = at < 0 ? 0 : (at + delta + list.length) % list.length;
    list[next].focus();
  };
  const onTriggerKey = (e: KeyboardEvent) => {
    if (page || disabled || e.key !== "ArrowDown") return;
    e.preventDefault();
    setOpen(true);
    requestAnimationFrame(() => focusRow(0));
  };
  const onContentKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      focusRow(e.key === "ArrowDown" ? 1 : -1);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };
  const pick = (tab: AppTab) => {
    setOpen(false);
    onSelect(tab);
  };

  return (
    <HoverCard
      open={open && !disabled}
      onOpenChange={(next) => setOpen(next && !disabled)}
      openDelay={300}
      closeDelay={120}
    >
      <HoverCardTrigger asChild onKeyDown={onTriggerKey}>
        {children}
      </HoverCardTrigger>
      {page ? (
        <HoverCardContent
          align="start"
          sideOffset={6}
          className="w-auto px-2.5 py-1.5 text-xs font-medium"
        >
          {label}
        </HoverCardContent>
      ) : (
        <HoverCardContent
          ref={contentRef}
          align="start"
          sideOffset={6}
          onKeyDown={onContentKey}
          data-testid="group-hover-card"
          className="w-72 p-0"
        >
          <div className="flex items-center gap-2.5 px-3 pt-3 pb-2">
            <TabMark
              tab={groupMark(group)}
              size="lg"
              className="size-8 text-xs"
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium">{label}</span>
              <span className="truncate text-xs text-muted-foreground">
                {subtitle ?? `${n} open ${n === 1 ? "tab" : "tabs"}`}
              </span>
            </div>
          </div>
          <div className="px-1 pb-1">
            {group.tabs.map((tab) => (
              <Row
                key={tab.id}
                tab={tab}
                signals={signalsFor(tab.id)}
                running={isRunning(tab.id)}
                onOpen={() => pick(tab)}
                onReview={(item) => {
                  setOpen(false);
                  navigate(itemRoute(item), { state: OPEN_THREAD_STATE });
                }}
              />
            ))}
          </div>
          <div className="flex h-8 items-center justify-between border-t px-3 text-xs text-muted-foreground">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onCloseGroup();
              }}
              className="rounded-sm hover:text-foreground focus-visible:text-foreground focus-visible:outline-none"
            >
              {n === 1 ? "Close tab" : `Close ${n} tabs`}
            </button>
            <span>
              <kbd className="font-sans">↵</kbd> Open
            </span>
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  );
};
